import { Menu, MenuItem } from '@mui/material';
import { useParams } from 'react-router-dom';
import { useState } from 'react';

import {
  Divider,
  IconButton,
  TableCell,
  TableRow,
  Typography,
  MoreVertIcon,
  InfoOutlinedIcon,
} from '@occc-app/design-system';
import {
  Retailer_Network_User_Role,
  RetailerNetworkUserFragment,
  RetailerNetworkUserUpdate,
} from '@occc/graphql';
import { Can } from '@occc-app/App/PolicyBasedAccessControl';
import {
  UpdateRetailerNetworkUser,
  RemoveUserFromRetailerNetwork,
} from '@occc-app/App/PolicyBasedAccessControl/userActions';
import { formatPhoneNumberNationalForUS } from '@occc/utils-phone-number';

import { userRolesDropdownOptions } from './location-users-table.constants';
import { useGetIsUserPermittedToEditRole } from './use-get-is-user-permitted-to-edit-role.hook';

import { mapRetailerNetworkUserRoleToText } from '../../../retailer-connect.utils';
import { InheritedLocationInfoDialog } from '../../../components/inherited-location-info-dialog.component';
import RetailerNetworkUserTableChangeRoleDropdown from '../../../components/retailer-network-user-table-change-role-dropdown.component';
import { useRetailerNetworkUserSelector } from '../../../retailer-network-context/use-retailer-network-user.selector';

type LocationUsersTableRowProps = {
  item: RetailerNetworkUserFragment;
  isPhoneNumberVisible?: boolean;
  handleRedirectToUserDetailsPage: (userUuid: string) => void;
  handleRemoveUserFromLocation: (userUuid: string) => void;
  handleRemoveUserFromRetailerNetwork: (openCommerceUserUuid: string) => void;
  handleUpdateRetailerNetworkUserRole: (input: RetailerNetworkUserUpdate) => Promise<void>;
  isRetailerNetworkUserUpdateInProgress: boolean;
};

type MenuItem = 'userDetails' | 'removeUserFromLocation' | 'removeUserFromRetailerNetwork';

export const LocationUsersTableRow = ({
  item,
  isPhoneNumberVisible = false,
  handleRedirectToUserDetailsPage,
  handleRemoveUserFromLocation,
  handleRemoveUserFromRetailerNetwork,
  handleUpdateRetailerNetworkUserRole,
  isRetailerNetworkUserUpdateInProgress,
}: LocationUsersTableRowProps) => {
  const { networkId } = useParams<{ networkId: string }>();
  const { isRetailerNetworkSiteAdmin } = useRetailerNetworkUserSelector();
  const [isInheritedLocationInfoModalOpen, setIsInheritedLocationInfoModalOpen] =
    useState<boolean>(false);
  const [actionsForUuid, setActionsForUuid] = useState<string | null>();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const {
    name,
    emailAddress,
    phoneNumber,
    role,
    uuid,
    openCommerceUser,
    retailerNetworkLocations,
  } = item;

  const isLocationInheritedFromGroup =
    !!retailerNetworkLocations?.[0]?.userAssociationInheritedFromLocationGroup;

  const isTargetUserRetailerNetworkSiteAdmin = role === Retailer_Network_User_Role.SiteAdmin;

  // Later we will add a separate policy which includes role in conditions
  // but for now we have to do that on FE side.
  const isUserPermittedToEditRole = useGetIsUserPermittedToEditRole(networkId, role);

  const handleClose = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(null);
    setActionsForUuid(null);
  };

  const getHandlerForAdditionalActions =
    (uuid: string) => (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();

      setAnchorEl(event.currentTarget);
      setActionsForUuid(uuid);
    };

  const onInheritedLocationInfoClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    handleClose(event);
    setIsInheritedLocationInfoModalOpen(true);
  };

  const onInheritedLocationInfoModalClose = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setIsInheritedLocationInfoModalOpen(false);
  };

  const onMenuNavigationClick = (itemName: MenuItem) => (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();

    if (itemName === 'userDetails') {
      handleRedirectToUserDetailsPage(uuid);
    }

    if (itemName === 'removeUserFromLocation') {
      return !isLocationInheritedFromGroup
        ? handleRemoveUserFromLocation(uuid)
        : onInheritedLocationInfoClick(event);
    }

    if (itemName === 'removeUserFromRetailerNetwork' && openCommerceUser) {
      handleRemoveUserFromRetailerNetwork(openCommerceUser.uuid);
    }

    handleClose(event);
  };

  const onRowClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    handleRedirectToUserDetailsPage(uuid);
  };

  return (
    <TableRow key={uuid} onClick={onRowClick}>
      <TableCell>{name}</TableCell>
      <TableCell>{emailAddress}</TableCell>
      {isPhoneNumberVisible && (
        <TableCell>{formatPhoneNumberNationalForUS(phoneNumber || '')}</TableCell>
      )}
      <TableCell>
        {isUserPermittedToEditRole ? (
          <RetailerNetworkUserTableChangeRoleDropdown
            userRole={role}
            userRolesOptions={userRolesDropdownOptions}
            onRoleChange={(userRole) =>
              handleUpdateRetailerNetworkUserRole({
                retailerNetworkUserUuid: uuid,
                role: userRole,
              })
            }
            isRetailerNetworkUserUpdateInProgress={isRetailerNetworkUserUpdateInProgress}
          />
        ) : (
          <Typography variant='body2'>{mapRetailerNetworkUserRoleToText[role]}</Typography>
        )}
      </TableCell>
      <TableCell align='right'>
        <IconButton onClick={getHandlerForAdditionalActions(uuid)} sx={{ p: 0 }}>
          <MoreVertIcon />
        </IconButton>
        <Menu
          open={uuid === actionsForUuid}
          onClose={handleClose}
          anchorEl={anchorEl}
          PaperProps={{
            sx: {
              borderRadius: '4px',
            },
          }}
        >
          <MenuItem onClick={onMenuNavigationClick('userDetails')}>View Details</MenuItem>

          <Can I={UpdateRetailerNetworkUser({ uuid: networkId || '' })}>
            {isRetailerNetworkSiteAdmin && isTargetUserRetailerNetworkSiteAdmin
              ? null
              : [
                  <Divider key='divider' />,
                  <MenuItem
                    key='removeUserFromLocation'
                    onClick={onMenuNavigationClick('removeUserFromLocation')}
                  >
                    {isLocationInheritedFromGroup ? (
                      <>
                        <Typography
                          color='textSecondary'
                          sx={{ cursor: 'not-allowed' }}
                          variant='body2'
                        >
                          Remove User from Location
                        </Typography>
                        <InfoOutlinedIcon color='primary' sx={{ ml: 1 }} />
                      </>
                    ) : (
                      'Remove User from Location'
                    )}
                  </MenuItem>,
                ]}
          </Can>
          <Can I={RemoveUserFromRetailerNetwork({ uuid: networkId || '' })}>
            <Divider key='divider' />
            <MenuItem
              key='removeUserFromRetailerNetwork'
              onClick={onMenuNavigationClick('removeUserFromRetailerNetwork')}
            >
              Remove User from Network
            </MenuItem>
          </Can>
        </Menu>
      </TableCell>
      {isLocationInheritedFromGroup && (
        <InheritedLocationInfoDialog
          open={isInheritedLocationInfoModalOpen}
          onClose={onInheritedLocationInfoModalClose}
        />
      )}
    </TableRow>
  );
};
